import styled from '@emotion/styled';
import offset from 'dom-helpers/offset';
import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { OverlayContainer } from 'react-aria';

import { suPx } from '../../../theme/functions/su';
import { theme } from '../../../theme/theme';

const CONTEXT_MENU_CONTAINER_PADDING = suPx(2);
const DEFAULT_CONTEXT_MENU_CONTEXT = {
  overlayRef: { current: null },
  subMenus: new Set(),
  register() {},
  unregister() {},
  registerSubMenu() {},
  unregisterSubMenu() {},
  onContextMenu() {},
  onCloseRequest() {},
  getPosition() {
    return null;
  },
  onHoverStart() {},
};
const ContextMenuContext = createContext(DEFAULT_CONTEXT_MENU_CONTEXT);

/**
 * Provides the global context for context menu's
 */
export function ContextMenuContextProvider(props) {
  const overlayRef = useRef();
  const instanceRef = useRef({
    position: null,
  });

  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    instanceRef.current.position = position == null ? instanceRef.current.position : position;
  });

  const context = useMemo(() => {
    const contextMenus = new Set();
    const subMenus = new Set();

    return {
      overlayRef,
      subMenus,
      register(instance) {
        contextMenus.add(instance);
      },
      unregister(instance) {
        contextMenus.delete(instance);
      },
      registerSubMenu(instance) {
        subMenus.add(instance);
      },
      unregisterSubMenu(instance) {
        subMenus.delete(instance);
      },
      onContextMenu(event, targetInstance) {
        // Close all known submenu's
        subMenus.forEach((instance) => {
          instance.onClose();
        });

        let action = null;

        // Close any open context menu that is not equal to this context menu.
        contextMenus.forEach((instance) => {
          if (instance !== targetInstance) {
            instance.onClose();
            return;
          }

          instance.target = event.target;
          instance.type = event.type;

          // Attached to button.
          if (instance.isOpen === true && event.type !== 'contextmenu') {
            // If it was already open on a press close it.
            action = () => {
              instance.onClose();
              setPosition(null);
              return { exit: true };
            };
          } else {
            action = () => {
              instance.onOpen();
              return { exit: false };
            };
          }
        });

        const { exit } = action();

        if (exit === true) return;

        // We need to reset here because opening the same menu on a different spot needs these
        // values to be reset. If we omit this the bounds checking goes wrong.
        overlayRef.current.style.setProperty('--x-overflow', `0px`);
        overlayRef.current.style.setProperty('--y-overflow', `0px`);

        // Setting this to currentTarget causes issues with navigation items context menu's but I
        // dont think it should be event.target.
        const target = event.target;

        // Returns the offset of the target, including top and left positions, width and height.
        const targetOffset = offset(target);

        // Move it slightly down.
        let offsetY = 20;

        // Relative to event target this can be used in advanced flow for example to place something
        // e.g. a flow card.
        const x = event.clientX != null ? event.clientX - targetOffset.left + target.scrollLeft : 0;
        const y = event.clientY != null ? event.clientY - targetOffset.top + target.scrollTop : 0;

        // Works for now but should be something like anchor top/bottom.
        if (event.type !== 'contextmenu') {
          offsetY = offsetY + targetOffset.height / 2;
        }

        // Default anchor when not attached to button is event.clientX / event.clientY.
        const anchorX = event.clientX ?? targetOffset.left + targetOffset.width / 2;
        const anchorY = (event.clientY ?? targetOffset.top) + offsetY;

        // This forces the layout effect.
        setPosition({
          x,
          y,
          clientX: event.clientX,
          clientY: event.clientY,
          anchorX,
          anchorY,
        });
      },
      onCloseRequest() {
        // Called on interacted outside or escape.
        contextMenus.forEach((instance) => {
          instance.onClose();
        });
        setPosition(null);
      },
      getPosition() {
        return instanceRef.current.position;
      },
      onHoverStart(event, parent) {
        const ancestors = [];

        function traverse(parent) {
          if (parent != null) {
            ancestors.push(parent);
            traverse(parent.parent);
          }
        }

        traverse(parent);

        subMenus.forEach((instance) => {
          if (ancestors.includes(instance) === false) {
            instance.onClose();
          }
        });
      },
    };
  }, []);

  useEffect(() => {
    if (position == null) {
      return undefined;
    }

    if (typeof ResizeObserver !== 'function') {
      return undefined;
    }

    const overlayElement = overlayRef.current;

    if (overlayElement == null) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateOverlayBounds(overlayElement);
    });

    resizeObserver.observe(overlayElement);

    return function () {
      resizeObserver.unobserve(overlayElement);
      resizeObserver.disconnect();
    };
  }, [position]);

  // Bounds checking.
  useLayoutEffect(() => {
    if (position != null) {
      updateOverlayBounds(overlayRef.current);
    }
  }, [position]);

  return (
    <ContextMenuContext.Provider value={context}>
      <OverlayContainer>
        <ContextMenuContextProvider.Overlay
          ref={overlayRef}
          style={{
            '--anchor-y': `${position?.anchorY ?? 0}px`,
            '--anchor-x': `${position?.anchorX ?? 0}px`,
            '--x-overflow': '0px',
            '--y-overflow': '0px',

            '--display': position == null ? 'none' : 'block',
            '--top': `calc(var(--anchor-y) - var(--y-overflow))`,
            '--left': `calc(var(--anchor-x) - var(--x-overflow))`,
          }}
        ></ContextMenuContextProvider.Overlay>
      </OverlayContainer>

      {props.children}
    </ContextMenuContext.Provider>
  );
}

export function useContextMenuContext() {
  return useContext(ContextMenuContext);
}

function updateOverlayBounds(overlayElement) {
  if (overlayElement == null) {
    return;
  }

  const clientWidth = document.documentElement.clientWidth;
  const clientHeight = document.documentElement.clientHeight;
  const maxHeight = Math.max(clientHeight - CONTEXT_MENU_CONTAINER_PADDING * 2, 0);

  overlayElement.style.setProperty('--context-menu-max-height', `${maxHeight}px`);

  const overlayRect = overlayElement.getBoundingClientRect();
  const overlayHorizontalOffset = overlayRect.width / 2;

  const anchorX = getPixelStyleValue(overlayElement, '--anchor-x');
  const anchorY = getPixelStyleValue(overlayElement, '--anchor-y');

  const minTop = CONTEXT_MENU_CONTAINER_PADDING;
  const maxTop = Math.max(
    minTop,
    clientHeight - CONTEXT_MENU_CONTAINER_PADDING - overlayRect.height,
  );
  const minLeft = CONTEXT_MENU_CONTAINER_PADDING;
  const maxLeft = Math.max(
    minLeft,
    clientWidth - CONTEXT_MENU_CONTAINER_PADDING - overlayRect.width,
  );

  const nextTop = clampValue(overlayRect.top, minTop, maxTop);
  const nextLeft = clampValue(overlayRect.left, minLeft, maxLeft);

  const yOverflow = anchorY - nextTop;
  const xOverflow = anchorX - nextLeft - overlayHorizontalOffset;

  overlayElement.style.setProperty('--y-overflow', `${yOverflow}px`);
  overlayElement.style.setProperty('--x-overflow', `${xOverflow}px`);
}

function clampValue(value, minValue, maxValue) {
  return Math.min(Math.max(value, minValue), maxValue);
}

function getPixelStyleValue(element, propertyName) {
  const value = element.style.getPropertyValue(propertyName);

  return Number.parseFloat(value) || 0;
}

ContextMenuContextProvider.Overlay = styled.div`
  position: fixed;
  box-shadow: ${theme.boxShadow.default};
  background-color: ${theme.color.component};
  border-radius: ${theme.borderRadius.medium};

  display: var(--display);
  top: var(--top);
  left: var(--left);

  transform: translateX(-50%);

  z-index: 100000;
`;
